<template>
  <form class="ls-form" novalidate @submit.prevent="submit">
    <div class="ls-form__honeypot" aria-hidden="true">
      <label for="ls-company">Company</label>
      <input id="ls-company" v-model="form.company" name="company" type="text" tabindex="-1" autocomplete="off">
    </div>

    <div class="ls-form__grid">
      <label v-for="field in fields" :key="field.key" class="ls-field">
        <span>{{ field.label }}</span>
        <div class="ls-field__control">
          <input
            v-model="form[field.key]"
            :type="field.type"
            :autocomplete="field.autocomplete"
            :required="field.required"
            :inputmode="field.inputmode"
          >
          <button
            v-if="form[field.key]"
            type="button"
            :aria-label="`Clear ${field.label}`"
            @click="form[field.key] = ''"
          >
            <X aria-hidden="true" />
          </button>
        </div>
      </label>
    </div>

    <p v-if="error" class="ls-form__alert ls-form__alert--error" role="alert">{{ error }}</p>
    <p v-if="state === 'success'" class="ls-form__alert" role="status">
      Thank you. The line sheet will reach you shortly, and Kendrick will follow up personally.
    </p>

    <button class="ls-form__submit" type="submit" :disabled="state === 'submitting'">
      {{ state === 'submitting' ? 'Sending' : 'Request the line sheet' }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, onUnmounted } from 'vue'
import { X } from 'lucide-vue-next'
import { useBehavioralBotDetection } from '~/composables/useBehavioralBotDetection'

type FieldKey = 'boutiqueName' | 'city' | 'email' | 'website'

const route = useRoute()
const signalsKit = useSignalsKit()
const { initialize, cleanup, getBotData } = useBehavioralBotDetection()

const fields: Array<{ key: FieldKey, label: string, type: string, autocomplete: string, required: boolean, inputmode?: 'url' | 'email' }> = [
  { key: 'boutiqueName', label: 'Store or showroom', type: 'text', autocomplete: 'organization', required: true },
  { key: 'city', label: 'City', type: 'text', autocomplete: 'address-level2', required: true },
  { key: 'email', label: 'Email', type: 'email', autocomplete: 'email', required: true, inputmode: 'email' },
  { key: 'website', label: 'Website or Instagram', type: 'text', autocomplete: 'url', required: false }
]

const initialForm = { boutiqueName: '', city: '', email: '', website: '', company: '' }
const form = reactive({ ...initialForm })
const state = ref<'idle' | 'submitting' | 'success' | 'error'>('idle')
const error = ref('')

const submit = async () => {
  error.value = ''
  if (!form.boutiqueName.trim() || !form.city.trim() || !form.email.trim()) {
    state.value = 'error'
    error.value = 'Store, city and email are required.'
    return
  }

  state.value = 'submitting'
  try {
    const response = await $fetch<{ success?: boolean, data?: { id?: number } }>('/api/b2b-lead', {
      method: 'POST',
      body: {
        boutiqueName: form.boutiqueName,
        city: form.city,
        email: form.email,
        website: form.website,
        interest: 'Wholesale line sheet',
        requestType: 'line_sheet',
        requestedLineSheet: true,
        company: form.company,
        sourcePath: route.fullPath,
        botData: getBotData('wholesale_line_sheet_request')
      }
    })
    signalsKit.trackFormGoal({
      formType: 'b2b_lead',
      formLocation: 'wholesale_line_sheet_component',
      requestType: 'line_sheet',
      locale: 'en',
      leadId: response?.data?.id || null,
      properties: {
        page_path: route.path,
        route_full_path: route.fullPath,
        requested_line_sheet: true,
        has_website: Boolean(form.website.trim()),
        b2b_lead_id: response?.data?.id || null
      }
    })
    Object.assign(form, initialForm)
    state.value = 'success'
  } catch (e: any) {
    state.value = 'error'
    error.value = e?.data?.statusMessage || e?.statusMessage || 'The request could not be sent. Please try again.'
  }
}

onMounted(() => { void initialize() })
onUnmounted(() => cleanup())
</script>

<style scoped>
.ls-form { display: grid; gap: 1.75rem; max-width: 46rem; }
.ls-form__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.ls-form__grid { display: grid; gap: 1.5rem 2rem; grid-template-columns: repeat(auto-fit, minmax(min(100%, 18rem), 1fr)); }
.ls-field { display: grid; gap: 0.6rem; }
.ls-field > span { font-size: 0.62rem; letter-spacing: 0.3em; text-transform: uppercase; color: rgba(26, 23, 20, 0.6); }
.ls-field__control { position: relative; display: flex; align-items: center; border-bottom: 1px solid rgba(26, 23, 20, 0.3); }
.ls-field__control:focus-within { border-bottom-color: #1a1714; }
.ls-field__control input { flex: 1; min-width: 0; border: 0; background: transparent; padding: 0.7rem 0; font-size: 1rem; color: #1a1714; outline: none; }
.ls-field__control button { display: inline-flex; width: 1.75rem; height: 1.75rem; align-items: center; justify-content: center; border: 0; background: transparent; color: rgba(26, 23, 20, 0.55); cursor: pointer; }
.ls-field__control button:hover { color: #1a1714; }
.ls-field__control button svg { width: 0.95rem; height: 0.95rem; }
.ls-form__alert { margin: 0; font-size: 0.95rem; line-height: 1.6; color: #31483d; }
.ls-form__alert--error { color: #8f2020; }
.ls-form__submit { justify-self: start; min-height: 3rem; white-space: nowrap; border: 0; border-radius: 999px; background: #11100e; padding: 0.85rem 2rem; font-size: 0.7rem; letter-spacing: 0.28em; text-transform: uppercase; color: #f6f1ea; cursor: pointer; transition: background 0.2s; }
.ls-form__submit:hover { background: #000; }
.ls-form__submit:disabled { opacity: 0.6; cursor: wait; }
</style>
